import { useDisclosure } from "@/hooks";
import {
	useAssignRoomsToMeditationRetreat,
	useGetRooms,
} from "@/hooks/query-hooks/useRoom";
import { cn } from "@/libs/cn";
import { useNotificationStore } from "@/states/zustand/notification";
import { PlusOutlined } from "@ant-design/icons";
import {
	Badge,
	Button,
	Checkbox,
	Empty,
	Form,
	Modal,
	Segmented,
	Space,
	Tag,
} from "antd";
import { useMemo, useState } from "react";
import { PageTitle } from "../common";
import { RoomCard } from "../Rooms/RoomCard";

export function AssignRoomModal({
	retreat,
	assignedRooms,
}: {
	retreat: MeditationRetreat;
	assignedRooms: Room[];
}) {
	const [open, { toggle }] = useDisclosure();
	const [availability, setAvailability] = useState<boolean>(true);

	const { data: allRooms = [], isLoading } = useGetRooms(open);

	const filteredRooms = useMemo(() => {
		return allRooms.filter((it) => !it.meditationEvent === availability);
	}, [availability, allRooms]);

	const availableRoomCount = useMemo(() => {
		return allRooms.filter((it) => !it.meditationEvent).length;
	}, [allRooms]);

	const [form] = Form.useForm();
	const selectedRooms = Form.useWatch("rooms", form);

	const { mutate, isPending } = useAssignRoomsToMeditationRetreat();
	const onFinish = ({ rooms }: { rooms: number[] }) => {
		if (!rooms || rooms?.length === 0) {
			useNotificationStore.getState().setNotification({
				type: "warning",
				message: "Please select at least one room!",
			});
			return;
		}

		// re-add previously assigned rooms, so that they don't get removed when the new was assigned to meditation events. fxxkkk
		const roomIds = [assignedRooms?.map((it) => it.id), rooms].flat();
		mutate(
			{ retreatId: retreat.id, roomIds },
			{
				onSuccess: () => {
					toggle();
					form.resetFields();
					useNotificationStore.getState().setNotification({
						type: "success",
						message: "New rooms assigned successfully!",
					});
				},
			},
		);
	};

	return (
		<>
			<Button
				icon={<PlusOutlined />}
				size="large"
				shape="circle"
				type="primary"
				className="bg-primary-gradient"
				onClick={toggle}
			/>
			<Modal
				open={open}
				title={<PageTitle>Add rooms for {retreat.title}</PageTitle>}
				onCancel={toggle}
				okButtonProps={{
					form: "add-rooms-form",
					className: cn("bg-primary-gradient rounded-lg!"),
					htmlType: "submit",
					loading: isPending,
				}}
				okText="Confirm"
				cancelButtonProps={{
					type: "text",
					disabled: isPending,
				}}
				width={800}
				classNames={{
					body: "space-y-6! overflow-y-scroll overflow-x-clip max-h-[600px]",
				}}
				styles={{
					body: {
						scrollbarWidth: "none",
					},
				}}
				loading={isLoading}
			>
				<Space
					className="w-full! sticky top-0 z-10  py-4 backdrop-blur-xs bg-white"
					direction="vertical"
					size={"middle"}
				>
					<Space align="center" className="w-full! justify-between">
						<Space size={"small"}>
							<p className="font-poppins">
								Total:{" "}
								<Tag color="green-inverse">
									<strong>{allRooms?.length}</strong>
								</Tag>
							</p>
							<Badge color="lightgray" />
							<p className="font-poppins">
								Selected:{" "}
								<Tag color="var(--color-f80)">
									<strong>
										{selectedRooms?.length || 0}
									</strong>
								</Tag>
							</p>
						</Space>
						<Space align="center">
							{[
								{
									label: "Selected",
									bgColor: "bg-f80",
								},
							].map((it) => (
								<Space key={it.label}>
									<div
										className={cn(
											"size-3 rounded-xs border border-black/20",
											it.bgColor,
										)}
									/>
									<p className="font-poppins text-gray-500">
										{it.label}
									</p>
								</Space>
							))}
						</Space>
					</Space>
					<Segmented
						options={[
							{
								label: `Available - ${availableRoomCount} `,
								value: true,
							},
							{
								label: `Unavailable - ${allRooms.length - availableRoomCount}`,
								value: false,
							},
						]}
						block
						onChange={(value) => setAvailability(value)}
						value={availability}
						size="large"
					/>
				</Space>

				{filteredRooms?.length > 0 ? (
					<Form form={form} onFinish={onFinish} name="add-rooms-form">
						<Form.Item name={"rooms"}>
							<Checkbox.Group className="w-full!">
								<section className="grid grid-cols-3 gap-5 w-full">
									{filteredRooms?.map((it, idx) => {
										const { id, meditationEvent } = it;

										const isAvailable = !meditationEvent;

										return (
											<Checkbox
												value={id}
												key={id}
												className="w-full! [&>.ant-checkbox]:hidden! [&>.ant-checkbox-label]:w-full! [&>.ant-checkbox-label]:px-0! [&>.ant-checkbox-label]:h-full! group animate-fade-in aspect-4/3"
												style={{
													animationDelay:
														idx * 0.07 + "s",
												}}
												disabled={!isAvailable}
											>
												<RoomCard {...it} showRetreat />
											</Checkbox>
										);
									})}
								</section>
							</Checkbox.Group>
						</Form.Item>
					</Form>
				) : (
					<Empty
						description={"No rooms found!"}
						className="font-poppins!"
					/>
				)}
			</Modal>
		</>
	);
}
